<template>
  <div class="environment-monitor">
    <DetailStats
      :title="title"
      :basic-stats="basicStats"
      :chart-groups="chartGroups"
      :trend-data="pm25TrendData"
      :format-value="formatMetricValue"
      :get-color="getMetricColor"
      :should-hide-unit="isWindDirection"
    />
  </div>
</template>

<script>
import DetailStats from './DetailStats.vue'
import { communityStore } from '../stores/community'
import { getCommunityConfig, COMMUNITIES } from '../utils/communityConfig'
import { REFRESH_CONFIG } from '../utils/refreshConfig'
import request from '@/utils/request'
import { getCurrentCommunity } from '@/utils/vc'

export default {
  name: 'EnvironmentMonitor',
  components: {
    DetailStats
  },
  data() {
    return {
      communityStore,
      title: '社区环境监测',
      envMetrics: [],
      pm25TrendData: null,
      chartGroups: [
        {
          title: '大气压力',
          fields: ['atmPress']
        },
        {
          title: '光照度',
          fields: ['illumination']
        },
        {
          title: '气象指标',
          fields: ['windSpeed', 'windDir', 'atmTemp', 'atmHum']
        },
        {
          title: '空气质量与环境噪声',
          fields: ['pm25', 'pm10', 'noise']
        }
      ],
      environmentTimer: null
    }
  },
  computed: {
    basicStats() {
      return this.envMetrics.map(metric => ({
        ...metric,
        colorType: this.getMetricColorType(metric.label, metric.value)
      }))
    }
  },
  watch: {
    'communityStore.currentCommunity'() {
      this.loadEnvironmentData()
    }
  },
  mounted() {
    this.loadEnvironmentData()
    this.environmentTimer = setInterval(() => {
      this.loadEnvironmentData()
    }, REFRESH_CONFIG.ENVIRONMENT_MONITOR)
  },
  methods: {
    isWindDirection(label) {
      return label && label.includes('风向')
    },
    getWindDirectionEnum(degrees) {
      const numValue = parseFloat(degrees) || 0
      if (numValue >= 0 && numValue <= 7 && numValue === Math.floor(numValue)) {
        return Math.floor(numValue)
      }
      let normalized = numValue % 360
      if (normalized < 0) normalized += 360
      if (normalized >= 337.5 || normalized < 22.5) return 0
      if (normalized >= 22.5 && normalized < 67.5) return 1
      if (normalized >= 67.5 && normalized < 112.5) return 2
      if (normalized >= 112.5 && normalized < 157.5) return 3
      if (normalized >= 157.5 && normalized < 202.5) return 4
      if (normalized >= 202.5 && normalized < 247.5) return 5
      if (normalized >= 247.5 && normalized < 292.5) return 6
      if (normalized >= 292.5 && normalized < 337.5) return 7
      return 0
    },
    getWindDirectionName(enumValue) {
      const windDirectionMap = {
        0: '北风', 1: '东北风', 2: '东风', 3: '东南风',
        4: '南风', 5: '西南风', 6: '西风', 7: '西北风'
      }
      return windDirectionMap[enumValue] || '未知'
    },
    formatMetricValue(label, value) {
      if (this.isWindDirection(label)) {
        const enumValue = this.getWindDirectionEnum(value)
        return this.getWindDirectionName(enumValue)
      }
      return value
    },
    getMetricColorType(label, value) {
      const numValue = parseFloat(value) || 0
      if (label.includes('风速')) {
        if (numValue <= 5.4) return 'green'
        if (numValue <= 10.7) return 'blue'
        if (numValue <= 13.8) return 'yellow'
        return 'red'
      } else if (label.includes('风向')) {
        return 'blue'
      } else if (label.includes('大气温度')) {
        if (numValue >= 18 && numValue <= 26) return 'green'
        if ((numValue >= 10 && numValue < 18) || (numValue > 26 && numValue <= 30)) return 'blue'
        if ((numValue >= 0 && numValue < 10) || (numValue > 30 && numValue <= 35)) return 'yellow'
        return 'red'
      } else if (label.includes('大气湿度')) {
        if (numValue >= 40 && numValue <= 60) return 'green'
        if ((numValue >= 30 && numValue < 40) || (numValue > 60 && numValue <= 70)) return 'blue'
        if ((numValue >= 20 && numValue < 30) || (numValue > 70 && numValue <= 80)) return 'yellow'
        return 'red'
      } else if (label.includes('大气压力')) {
        const standardPressure = 1013
        const diff = Math.abs(numValue - standardPressure)
        if (diff <= 10) return 'green'
        if (diff <= 20) return 'blue'
        if (diff <= 30) return 'yellow'
        return 'red'
      } else if (label.includes('PM2.5')) {
        if (numValue <= 35) return 'green'
        if (numValue <= 75) return 'yellow'
        return 'red'
      } else if (label.includes('PM10')) {
        if (numValue <= 50) return 'green'
        if (numValue <= 150) return 'yellow'
        return 'red'
      } else if (label.includes('光照度')) {
        if (numValue >= 1000 && numValue <= 100000) return 'green'
        if ((numValue >= 500 && numValue < 1000) || (numValue > 100000 && numValue <= 150000)) return 'blue'
        if ((numValue >= 200 && numValue < 500) || (numValue > 150000 && numValue <= 200000)) return 'yellow'
        return 'red'
      } else if (label.includes('环境噪声') || label.includes('噪声')) {
        if (numValue >= 30 && numValue <= 50) return 'green'
        if (numValue > 50 && numValue <= 70) return 'blue'
        if (numValue > 70 && numValue <= 90) return 'yellow'
        return 'red'
      }
      return 'blue'
    },
    getMetricColor(label, value) {
      const colorType = this.getMetricColorType(label, value)
      const colorMap = {
        'green': '#4caf50',
        'blue': '#00d4ff',
        'yellow': '#ffc107',
        'red': '#ff5a5a'
      }
      return colorMap[colorType] || '#00d4ff'
    },
    loadEnvironmentData() {
      const cfg = getCommunityConfig(this.communityStore.currentCommunity)
      if (cfg.data && cfg.data.environment) {
        this.envMetrics = cfg.data.environment.metrics || []
        this.pm25TrendData = cfg.data.environment.pm25Trend || null
        if (this.pm25TrendData && this.pm25TrendData.series) {
          this.pm25TrendData.series.forEach(seriesItem => {
            const metric = this.envMetrics.find(m => m.label === seriesItem.name)
            if (metric && metric.value) {
              let currentValue = parseFloat(metric.value)
              if (isNaN(currentValue)) {
                currentValue = parseFloat(String(metric.value).replace(/[^\d.-]/g, ''))
              }
              if (!isNaN(currentValue) && seriesItem.data && seriesItem.data.length > 0) {
                seriesItem.data[seriesItem.data.length - 1] = currentValue
              }
            }
          })
        }
      } else {
        this.envMetrics = []
        this.pm25TrendData = null
      }

      let communityId = null
      try {
        const currentCommunity = getCurrentCommunity()
        if (currentCommunity && currentCommunity.communityId) {
          communityId = currentCommunity.communityId
        }
      } catch (e) {
        console.warn('获取 communityId 失败:', e)
        return
      }

      if (!communityId) {
        return
      }

      request({
        url: '/app/bigScreen/getEnvironmentMonitor',
        method: 'get',
        params: {
          communityId: communityId,
          deviceCode: 'D1647X4DWH2N'
        },
        showLoading: false
      }).then(response => {
        const res = response.data
        if (res && res.code === 0 && res.data) {
          const data = res.data
          const currentCommunityName = this.communityStore.currentCommunity
          const cfg = COMMUNITIES[currentCommunityName]

          if (!cfg || !cfg.data || !cfg.data.environment || !cfg.data.environment.metrics) {
            console.warn('未找到环境监测配置:', currentCommunityName)
            return
          }

          if (data.environment && data.environment.metrics) {
            const backendMetrics = data.environment.metrics
            cfg.data.environment.metrics.forEach(configMetric => {
              if (configMetric.field) {
                const backendMetric = backendMetrics.find(bm => bm.field === configMetric.field)
                if (backendMetric && backendMetric.value !== undefined) {
                  configMetric.value = String(backendMetric.value || '0')
                }
              }
            })
          }

          if (data.environment && data.environment.pm25Trend) {
            const backendTrend = data.environment.pm25Trend
            if (backendTrend.series && cfg.data.environment.pm25Trend && cfg.data.environment.pm25Trend.series) {
              backendTrend.series.forEach(backendSeries => {
                const configSeries = cfg.data.environment.pm25Trend.series.find(cs => cs.field === backendSeries.field)
                if (configSeries) {
                  configSeries.data = backendSeries.data || []
                } else {
                  const metric = cfg.data.environment.metrics.find(m => m.field === backendSeries.field)
                  if (metric) {
                    cfg.data.environment.pm25Trend.series.push({
                      name: metric.label,
                      unit: metric.unit,
                      field: backendSeries.field,
                      data: backendSeries.data || [],
                      min: 0,
                      max: 100
                    })
                  }
                }
              })
              cfg.data.environment.pm25Trend.series.forEach(configSeries => {
                const backendSeries = backendTrend.series.find(bs => bs.field === configSeries.field)
                if (!backendSeries && configSeries.data.length === 0) {
                  configSeries.data = new Array(24).fill(0)
                }
              })
            } else {
              if (backendTrend.series && cfg.data.environment.metrics) {
                const newSeries = backendTrend.series.map(backendSeries => {
                  const metric = cfg.data.environment.metrics.find(m => m.field === backendSeries.field)
                  return {
                    name: metric ? metric.label : backendSeries.field,
                    unit: metric ? metric.unit : '',
                    field: backendSeries.field,
                    data: backendSeries.data || [],
                    min: 0,
                    max: 100
                  }
                })
                const allFields = ['windSpeed', 'windDir', 'atmTemp', 'atmHum', 'atmPress', 'pm25', 'pm10', 'illumination', 'noise']
                allFields.forEach(field => {
                  if (!newSeries.find(s => s.field === field)) {
                    const metric = cfg.data.environment.metrics.find(m => m.field === field)
                    if (metric) {
                      newSeries.push({
                        name: metric.label,
                        unit: metric.unit,
                        field: field,
                        data: new Array(24).fill(0),
                        min: 0,
                        max: 100
                      })
                    }
                  }
                })
                cfg.data.environment.pm25Trend = {
                  hours: backendTrend.hours || cfg.data.environment.pm25Trend.hours,
                  series: newSeries
                }
              } else {
                cfg.data.environment.pm25Trend = {
                  hours: backendTrend.hours || cfg.data.environment.pm25Trend.hours,
                  series: backendTrend.series || []
                }
              }
            }
          }

          if (cfg.data.environment.pm25Trend && cfg.data.environment.pm25Trend.series) {
            cfg.data.environment.pm25Trend.series.forEach(seriesItem => {
              const metric = cfg.data.environment.metrics.find(m => m.field === seriesItem.field)
              if (metric && metric.value) {
                let currentValue = parseFloat(metric.value)
                if (isNaN(currentValue)) {
                  currentValue = parseFloat(String(metric.value).replace(/[^\d.-]/g, ''))
                }
                if (!isNaN(currentValue) && seriesItem.data && seriesItem.data.length > 0) {
                  seriesItem.data[seriesItem.data.length - 1] = currentValue
                }
              }
            })
          }

          this.envMetrics = cfg.data.environment.metrics
          this.pm25TrendData = cfg.data.environment.pm25Trend
        }
      }).catch(error => {
        console.warn('获取环境监测数据失败，使用默认配置数据:', error)
      })
    }
  },
  beforeDestroy() {
    if (this.environmentTimer) {
      clearInterval(this.environmentTimer)
      this.environmentTimer = null
    }
  }
}
</script>

<style lang="scss" scoped>
.environment-monitor {
  height: 100%;
  width: 100%;
}
</style>


